import { useCallback, useEffect, useMemo, useState, type RefObject } from "react";
import { IconChevronDown } from "@tabler/icons-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/lilia-ui/popover";
import type { CanvasLook } from "@/lib/themes/look";
import { kickerApplies, kickerChapters, kickerPlace, type HeadingBox, type KickerChapter, type KickerPlace } from "@/lib/themes/kicker";
import { inkOn } from "@/lib/themes/tables";
import { cn } from "@/lib/cn";
import { testIds } from "@/testIds";

interface BlockLike {
  id: string;
  type: string;
  content?: Record<string, unknown> | null;
}

/** The pill sits 12 px below the canvas top and is 28 px tall: headings under it count as passed. */
const PILL_BAND = 48;
/** Below this the pill drops the subsection. */
const NARROW = 700;
const SAFE_ID = /^[A-Za-z0-9_-]+$/;

/** ⌘⇧O on a Mac, Ctrl+Shift+O elsewhere. */
export function isOutlineShortcut(e: Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">): boolean {
  return (e.metaKey || e.ctrlKey) && e.shiftKey && !e.altKey && e.key.toLowerCase() === "o";
}

/**
 * The sticky kicker (document themes, 2d): where you are, once the current
 * chapter's heading has scrolled out of view. A tab with the chapter's number,
 * its name, and "› the subsection you are in". Click (or ▾, or ⌘⇧O from
 * anywhere) opens the outline at the current section; picking scrolls there.
 *
 * The pill is editor chrome. Only the tab takes the document's colour — the
 * chapter colour in Index, the theme's kicker colour otherwise — with white ink only
 * where it holds 4.5:1; Classic's tab is neutral. Hidden in a document with
 * fewer than two top-level sections.
 */
export function CanvasKicker({
  blocks,
  look,
  scrollRef,
}: {
  blocks: readonly BlockLike[];
  look: CanvasLook | null;
  /** The canvas's scrolling element. */
  scrollRef: RefObject<HTMLElement | null>;
}) {
  const chapters = useMemo(() => kickerChapters(blocks), [blocks]);
  const applies = kickerApplies(chapters);
  const [place, setPlace] = useState<KickerPlace | null>(null);
  const [open, setOpen] = useState(false);
  const [narrow, setNarrow] = useState(false);

  const measure = useCallback(() => {
    const sc = scrollRef.current;
    if (!sc || !applies) {
      setPlace(null);
      return;
    }
    const top = sc.getBoundingClientRect().top;
    const boxOf = (id: string): HeadingBox | null => {
      if (!SAFE_ID.test(id)) return null;
      const el = sc.querySelector(`[data-block-id="${id}"]`);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return { top: r.top - top, bottom: r.bottom - top };
    };
    const next = kickerPlace(chapters, boxOf, PILL_BAND);
    // Keep the same object while nothing changed, so a scroll frame costs no render.
    setPlace((prev) => (prev && next && prev.chapter === next.chapter && prev.sub === next.sub && prev.out === next.out ? prev : next));
  }, [scrollRef, chapters, applies]);

  // Follow the scroll (one measure per frame) and the canvas's width. A
  // passive effect: the scroller comes after the kicker in the tree, so its
  // ref is only attached once every layout effect has run.
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        measure();
      });
    };
    measure();
    sc.addEventListener("scroll", onScroll, { passive: true });
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        setNarrow(sc.clientWidth > 0 && sc.clientWidth < NARROW);
        onScroll();
      });
      ro.observe(sc);
    }
    return () => {
      sc.removeEventListener("scroll", onScroll);
      ro?.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [scrollRef, measure]);

  // ⌘⇧O opens the outline from anywhere on the page.
  useEffect(() => {
    if (!applies) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isOutlineShortcut(e)) return;
      e.preventDefault();
      measure();
      setOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [applies, measure]);

  if (!applies) return null;

  // Opened by the shortcut above the first chapter: the outline still opens, at the first.
  const shown = place ?? (open ? { chapter: chapters[0], sub: null, out: false } : null);
  const visible = !!shown && (open || shown.out);
  const fill = shown ? tabFill(look, shown.chapter) : null;

  const scrollTo = (id: string) => {
    setOpen(false);
    if (!SAFE_ID.test(id)) return;
    const el = scrollRef.current?.querySelector(`[data-block-id="${id}"]`);
    if (el && "scrollIntoView" in el) (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      data-testid={testIds.kicker.root}
      data-visible={visible ? "true" : "false"}
      data-chapter-id={shown?.chapter.id}
      aria-hidden={visible ? undefined : true}
      className="pointer-events-none absolute left-1/2 top-3 z-[5] flex max-w-[calc(100%-32px)] -translate-x-1/2 justify-center"
      style={{
        opacity: visible ? 1 : 0,
        visibility: visible ? "visible" : "hidden",
        transition: "opacity 120ms ease, visibility 120ms",
      }}
    >
      {shown && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              data-testid={testIds.kicker.toggle}
              aria-label={`You are in ${shown.chapter.number ? `${shown.chapter.number} ` : ""}${shown.chapter.title}. Open the outline`}
              aria-haspopup="dialog"
              tabIndex={visible ? 0 : -1}
              className="pointer-events-auto inline-flex h-7 min-w-0 max-w-full items-center gap-2 rounded-[14px] border border-[var(--lilia-border-strong)] bg-[var(--lilia-surface)] py-0 pl-1 pr-1.5 text-[12px] font-semibold text-[color:var(--lilia-text)] shadow-[0_4px_14px_rgba(16,24,40,.12)] focus-visible:outline-2 focus-visible:outline-[var(--lilia-ring)]"
            >
              {shown.chapter.number && (
                <span
                  data-part="tab"
                  data-fill={fill ?? undefined}
                  className={cn(
                    "inline-flex h-5 shrink-0 items-center rounded-[10px] px-2 text-[10.5px] font-extrabold tracking-[.06em]",
                    !fill && "bg-[var(--lilia-bg-secondary)] text-[color:var(--lilia-text)]",
                  )}
                  style={fill ? { background: fill, color: inkOn(fill) } : undefined}
                >
                  {shown.chapter.number}
                </span>
              )}
              <span data-part="chapter" className="min-w-0 truncate">
                {shown.chapter.title}
              </span>
              {!narrow && shown.sub && (
                <span data-part="sub" className="min-w-0 truncate font-normal text-[color:var(--lilia-text-secondary)]">
                  › {shown.sub.number ? `${shown.sub.number} ` : ""}
                  {shown.sub.title}
                </span>
              )}
              <IconChevronDown size={13} aria-hidden className="shrink-0 text-[color:var(--lilia-text-secondary)]" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="center"
            sideOffset={6}
            data-testid={testIds.kicker.outline}
            aria-label="Outline"
            className="w-[320px] max-h-[60vh] overflow-y-auto rounded-[10px] border-[var(--lilia-border-strong)] p-1.5 thin-scrollbar"
            style={{ background: "var(--lilia-surface)", boxShadow: "0 16px 40px var(--lilia-shadow-lg)" }}
            onOpenAutoFocus={(e) => {
              // Land on the current section, not the first item.
              e.preventDefault();
              const current = (e.currentTarget as HTMLElement | null)?.querySelector<HTMLElement>('[data-current="true"]');
              current?.focus();
              if (current && "scrollIntoView" in current) current.scrollIntoView({ block: "nearest" });
            }}
          >
            <OutlineList chapters={chapters} look={look} currentId={shown.sub?.id ?? shown.chapter.id} onPick={scrollTo} />
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

/** The tab's fill: the chapter colour in Index, the theme's kicker colour elsewhere, none for Classic. */
export function tabFill(look: CanvasLook | null, chapter: Pick<KickerChapter, "id">): string | null {
  if (!look || look.theme === "classic") return null;
  if (look.headingColours.size) return look.headingColours.get(chapter.id)?.colour ?? null;
  return look.kicker || look.accent || null;
}

function OutlineList({
  chapters,
  look,
  currentId,
  onPick,
}: {
  chapters: readonly KickerChapter[];
  look: CanvasLook | null;
  currentId: string;
  onPick: (id: string) => void;
}) {
  return (
    <ul className="m-0 flex list-none flex-col p-0">
      {chapters.flatMap((c) => {
        const fill = tabFill(look, c);
        const rows = [
          <li key={c.id}>
            <OutlineItem id={c.id} current={c.id === currentId} onPick={onPick} indent={0}>
              {c.number && (
                <span
                  className={cn(
                    "inline-flex h-[18px] min-w-[20px] shrink-0 items-center justify-center rounded-[5px] px-1 text-[10.5px] font-bold",
                    !fill && "bg-[var(--lilia-bg-secondary)] text-[color:var(--lilia-text)]",
                  )}
                  style={fill ? { background: fill, color: inkOn(fill) } : undefined}
                >
                  {c.number}
                </span>
              )}
              <span className="min-w-0 truncate font-semibold">{c.title}</span>
            </OutlineItem>
          </li>,
        ];
        for (const s of c.subs) {
          if (s.level > 3) continue;
          rows.push(
            <li key={s.id}>
              <OutlineItem id={s.id} current={s.id === currentId} onPick={onPick} indent={s.level - 1}>
                {s.number && <span className="shrink-0 tabular-nums text-[color:var(--lilia-text-secondary)]">{s.number}</span>}
                <span className="min-w-0 truncate">{s.title}</span>
              </OutlineItem>
            </li>,
          );
        }
        return rows;
      })}
    </ul>
  );
}

function OutlineItem({
  id,
  current,
  indent,
  onPick,
  children,
}: {
  id: string;
  current: boolean;
  indent: number;
  onPick: (id: string) => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      data-testid={testIds.kicker.outlineItem}
      data-block-id={id}
      data-current={current ? "true" : undefined}
      aria-current={current ? "location" : undefined}
      onClick={() => onPick(id)}
      className={cn(
        "flex w-full min-w-0 items-center gap-2 rounded-[6px] py-1.5 pr-2 text-left text-[12.5px] text-[color:var(--lilia-text)] hover:bg-[var(--lilia-hover)] focus-visible:bg-[var(--lilia-hover)] focus-visible:outline-none",
        current && "bg-[var(--lilia-primary-subtle)] text-[color:var(--lilia-primary-ink)]",
      )}
      style={{ paddingLeft: 8 + indent * 14 }}
    >
      {children}
    </button>
  );
}
